import React, { useCallback, useRef, useState } from 'react';
import { View, Text } from 'react-native';
import { Editor } from 'slate';
import { useSlate } from 'slate-react';
import ReanimatedPicker, { Panel1, Swatches } from 'reanimated-color-picker';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';
import TouchableElement from '@components/common/touchableElement';
import Select from '@components/common/select';
import ModalWrapper from '@components/modal/ModalWrapper';

import { fullWidth } from '@components/frame';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import {
  CustomElementFormat,
  FONT_SIZE_OPTIONS,
  getHighlightedColor,
  getHighlightedFontSize,
  isAlignType,
  isBlockActive,
  isMarkActive,
  toggleBlock,
  toggleMark,
  useEditorConfig,
} from './config';
import type { ToolbarProps, MarkButtonProps, BlockButtonProps, WebColorPickerProps, ToolbarDividerProps } from './types';


const ColorPicker = ({ C, focus }: WebColorPickerProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const editor = useSlate();
  const { scale } = useEditorConfig();
  const containerRef = useRef<View>(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  const highlightedColor = getHighlightedColor(editor) ?? C('Label/Light/Secondary');

  const handleOpen = useCallback(() => {
    containerRef.current?.measure((x, y, width, height, pageX, pageY) => {
      // Adjust right position so modal's right edge aligns with button's right edge
      // Modal width is 300px, button width is 16px + margin 4px = 20px
      const modalWidth = scale(300);
      const buttonWidth = scale(20); // 16px width + 4px margin
      const right = fullWidth - pageX - width;

      setPosition({
        right: right - modalWidth + buttonWidth,
        top: pageY + height,
      });
      setOpen(true);
    });
  }, [scale]);

  return (
    <>
      <TouchableElement onPress={handleOpen}>
        {({ boxStyle }) => {
          return (
            <View
              ref={containerRef}
              style={[
                boxStyle,
                {
                  backgroundColor: highlightedColor ?? C('Label/Light/Secondary'),
                  width: scale(16),
                  height: scale(16),
                  borderRadius: '50%',
                  margin: scale(2),
                },
              ]}
            />
          );
        }}
      </TouchableElement>

      {open && (
        <ModalWrapper
          C={C}
          withPositionedContainer
          positionedContainerStyles={{ right: position.right, top: position.top }}
          isVisible={open}
          onBackdropPress={() => setOpen(false)}
          scale={scale}
        >
          <View
            style={{
              padding: scale(8),
              width: scale(300),
            }}
          >
            <ReanimatedPicker
              value={highlightedColor}
              onComplete={(value) => {
                Editor.addMark(editor, 'color', value.hex);
                focus();
              }}
              thumbSize={scale(24)}
              thumbShape="circle"
              style={{
                width: '100%',
                height: '100%',
              }}
            >
              <Panel1
                style={{
                  backgroundImage: `linear-gradient(to bottom, transparent, #000000), linear-gradient(to right, #ffffff, transparent)`,
                  marginBottom: scale(8),
                  borderWidth: scale(1),
                  borderStyle: 'solid',
                  borderColor: C('Hairline/Light/Transpar [mobile]'),
                }}
              />
              <Swatches />

              <TouchableElement onPress={() => setOpen(false)}>
                {({ boxStyle, pressHoverColor }) => {
                  return (
                    <View
                      style={[
                        boxStyle,
                        {
                          backgroundColor: C(pressHoverColor('Selected/Light/Left Pane Menu Item')),
                          width: '100%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          paddingVertical: scale(6.5),
                          borderRadius: scale(SCREEN_BORDER_RADIUS),
                        },
                      ]}
                    >
                      <Text style={{ color: C('Label/Light/AlwaysLight Primary') }}>{i18n.t('Confirm')}</Text>
                    </View>
                  );
                }}
              </TouchableElement>
            </ReanimatedPicker>
          </View>
        </ModalWrapper>
      )}
    </>
  );
};

const MarkButton = ({ format, icon, C, focus }: MarkButtonProps) => {
  const editor = useSlate();
  const { scale } = useEditorConfig();

  return (
    <TouchableElement
      onPress={() => {
        toggleMark(editor, format);
        focus();
      }}
    >
      {({ boxStyle, pressHoverColor }) => {
        const isActive = isMarkActive(editor, format);

        return (
          <View
            style={[
              boxStyle,
              {
                padding: scale(2),
                backgroundColor: isActive ? C(pressHoverColor('Selected/Light/Secondary')) : 'transparent',
                borderWidth: scale(0.5),
                borderColor: isActive ? C(pressHoverColor('Hairline/Light/No trans [mobile]')) : 'transparent',
                borderRadius: scale(4),
              },
            ]}
          >
            <Svg width={scale(16)} height={scale(16)} icon={icon} color="Gray/Light/1" />
          </View>
        );
      }}
    </TouchableElement>
  );
};

const BlockButton = ({ format, icon, C, focus }: BlockButtonProps) => {
  const editor = useSlate();
  const { scale } = useEditorConfig();

  return (
    <TouchableElement
      onPress={() => {
        toggleBlock(editor, format);
        focus();
      }}
    >
      {({ boxStyle, pressHoverColor }) => {
        const isActive = isBlockActive(editor, format, isAlignType(format) ? 'align' : 'type');

        return (
          <View
            style={[
              boxStyle,
              {
                padding: scale(2),
                backgroundColor: isActive ? C(pressHoverColor('Selected/Light/Secondary')) : 'transparent',
                borderWidth: scale(0.5),
                borderColor: isActive ? C('Hairline/Light/No trans [mobile]') : 'transparent',
                borderRadius: scale(4),
              },
            ]}
          >
            <Svg width={scale(16)} height={scale(16)} icon={icon} color="Gray/Light/1" />
          </View>
        );
      }}
    </TouchableElement>
  );
};

const ToolbarDivider = ({ C }: ToolbarDividerProps) => {
  const { scale } = useEditorConfig();

  return (
    <View style={{ position: 'relative' }}>
      <View
        style={{
          width: scale(1),
          height: scale(20 + 10 * 2),
          backgroundColor: C('Gray/Light/5'),
          position: 'absolute',
          transform: 'translateY(-25%)',
        }}
      />
    </View>
  );
};

const Toolbar: React.FC<ToolbarProps> = ({ C, focus }) => {
  const editor = useSlate();
  const { scale, font } = useEditorConfig();

  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: scale(12),
        borderTopWidth: scale(0.5),
        borderTopColor: C('Hairline/Light/Transpar [mobile]'),
        paddingHorizontal: scale(16),
        paddingVertical: scale(10),
      }}
    >
      <Select
        items={FONT_SIZE_OPTIONS}
        scale={scale}
        font={font}
        onChange={(item) => {
          Editor.addMark(editor, 'fontSize', item.value);
          setTimeout(focus, 0);
        }}
        selected={getHighlightedFontSize(editor)}
      />
      <ToolbarDivider C={C} />
      <ColorPicker C={C} focus={focus} />
      <ToolbarDivider C={C} />
      <MarkButton C={C} format="bold" icon="rteBold" focus={focus} />
      <MarkButton C={C} format="italic" icon="rteItalic" focus={focus} />
      <MarkButton C={C} format="underline" icon="rteUnderline" focus={focus} />
      <MarkButton C={C} format="strikethrough" icon="rteStrikethrough" focus={focus} />
      <ToolbarDivider C={C} />
      <BlockButton C={C} format="left" icon="rteAlignLeft" focus={focus} />
      <BlockButton C={C} format="center" icon="rteAlignCenter" focus={focus} />
      <BlockButton C={C} format="right" icon="rteAlignRight" focus={focus} />
      <ToolbarDivider C={C} />
      <BlockButton C={C} format="numbered-list" icon="rteOrderedList" focus={focus} />
      <BlockButton C={C} format="bulleted-list" icon="rteUnorderedList" focus={focus} />
      <ToolbarDivider C={C} />
    </View>
  );
};

export default ColorWrapper(Toolbar);
